import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Card, CardList, Skeleton, StartCard, StartCardList } from '@indipia/ui-core';
import { worklogApi } from '../api/worklogApi';
import type { DashboardSummary } from '../types/worklog.types';
import { clickable } from '../utils/clickable';

const BASE = '/agents/work-log';

type LoadState = 'loading' | 'ready' | 'error';

/** 숫자 바인딩 — undefined=로딩(스켈레톤), null=값없음('—'), number=값 */
function Num({ n }: { n: number | null | undefined }) {
  if (n === undefined) {
    return <Skeleton variant="text" width="1.5rem" height="0.8em" className="align-middle" />;
  }
  if (n === null) return <>—</>;
  return <>{n}</>;
}

// ── 아이콘 (화면기획안 인라인 SVG 이관) ──────────────────────────────
const stroke = {
  fill: 'none',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function GridIcon({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" stroke={color} {...stroke}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function ClipboardIcon({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" stroke={color} {...stroke}>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="13" y2="16" />
    </svg>
  );
}

function MessageIcon({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" stroke={color} {...stroke}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function GridSearchIcon({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" stroke={color} {...stroke}>
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}

function DocIcon({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" stroke={color} {...stroke}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="13" y2="17" />
    </svg>
  );
}

/** 아이콘 배경(색상 12% 틴트) */
function tint(colorVar: string) {
  return { background: `color-mix(in srgb, ${colorVar} 12%, transparent)` };
}

function WorkLogHome() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    worklogApi
      .getDashboardSummary(controller.signal)
      .then((res) => {
        setSummary(res.data.data);
        setState('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setState('error');
      });
    return () => controller.abort();
  }, [reloadKey]);

  const ready = state === 'ready';
  // undefined → 로딩 스켈레톤 / null → 값 없음('—')
  const val = (n: number | undefined): number | null | undefined =>
    ready ? (n ?? 0) : undefined;

  const go = (path: string) => navigate(`${BASE}/${path}`);

  return (
    <div className="h-full overflow-auto bg-surface">
        <div className="mx-auto max-w-[900px] px-8 py-10">
        {/* 인사 */}
        <p className="mb-8 text-body-md text-text-secondary">
          현장 방문 없이도, 현장에서 일어나는 일들을 확인해 보세요.
        </p>

        {state === 'error' && (
          <div className="mb-6 flex items-center justify-between gap-4 rounded-lg border border-border bg-surface-raised px-5 py-4">
            <span className="text-sm text-text-secondary">
              현황 정보를 불러오지 못했습니다.
            </span>
            <Button variant="secondary" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
              다시 시도
            </Button>
          </div>
        )}

        {/* ── 오늘 현황 ── */}
        <div className="mb-3 text-caption font-medium text-text-secondary">오늘 현황</div>
        <StartCardList>
          {/* 진행 중인 현장 — 표시 전용(이동·패널 없음) */}
          <StartCard
            icon={<GridIcon color="currentColor" />}
            label="진행 중인 현장"
            count={<Num n={val(summary?.inProgressWorklogCount)} />}
          />

          {/* 오늘의 작업요청 → 작업요청 목록 */}
          <StartCard
            icon={<ClipboardIcon color="currentColor" />}
            label="오늘의 작업요청"
            count={<Num n={val(summary?.todayWorkOrderCount)} />}
            onClick={() => go('workorder')}
          />
        </StartCardList>

        {/* ── 메뉴 (액션 카드) ── */}
        <div className="mb-3 mt-8 text-caption font-medium text-text-secondary">메뉴</div>
        <CardList minColWidth={320}>
          {/* 작업요청 → 작업요청 작성 */}
          <Card className="agent-card" {...clickable(() => go('workorder/new'))}>
            <div className="agent-card-head">
              <span
                className="flex h-09 w-09 shrink-0 items-center justify-center rounded-lg"
                style={tint('var(--color-warning)')}
              >
                <ClipboardIcon color="var(--color-warning)" />
              </span>
              <h4 className="agent-tit">작업요청</h4>
            </div>
            <p className="mb-1.5 text-caption font-semibold text-text">반장님께 작업 지시 전달</p>
            <p className="agent-desc flex-1">
              오늘 해야 할 작업을 작성하면 반장님 카카오톡으로 바로 전달됩니다.
            </p>
            <div className="meta items-center justify-between">
              <span className="text-caption text-text-secondary">
                오늘 작성 <strong className="font-bold text-text"><Num n={val(summary?.todayWorkOrderCount)} />건</strong>
              </span>
              <span className="text-xs font-bold text-[color:var(--color-warningStrong)]">작성하기 ›</span>
            </div>
          </Card>

          {/* 현장보고 → 현장보고 확인(목록, C-0) */}
          <Card className="agent-card" {...clickable(() => go('field-reports'))}>
            <div className="agent-card-head">
              <span
                className="flex h-09 w-09 shrink-0 items-center justify-center rounded-lg"
                style={tint('var(--color-success)')}
              >
                <MessageIcon color="var(--color-success)" />
              </span>
              <h4 className="agent-tit">현장보고</h4>
            </div>
            <p className="mb-1.5 text-caption font-semibold text-text">반장님 보고를 바로 확인</p>
            <p className="agent-desc flex-1">
              반장님이 올린 현장 사진과 보고 내용을 실시간으로 확인합니다.
            </p>
            <div className="meta items-center justify-between">
              <span className="text-caption text-text-secondary">
                오늘 수신 <strong className="font-bold text-text"><Num n={val(summary?.todayFieldReportCount)} />건</strong>
              </span>
              <span className="text-xs font-bold text-[color:var(--color-successStrong)]">확인하기 ›</span>
            </div>
          </Card>

          {/* 공사현황 */}
          <Card className="agent-card" {...clickable(() => go('site-reports'))}>
            <div className="agent-card-head">
              <span
                className="flex h-09 w-09 shrink-0 items-center justify-center rounded-lg"
                style={tint('var(--color-info)')}
              >
                <GridSearchIcon color="var(--color-info)" />
              </span>
              <h4 className="agent-tit">공사현황</h4>
            </div>
            <p className="mb-1.5 text-caption font-semibold text-text">현장 흐름을 한눈에 파악</p>
            <p className="agent-desc flex-1">
              작업요청·현장보고를 날짜별로 보고, 고객에게 공유할 내용을 선별합니다.
            </p>
            <div className="meta items-center justify-between">
              <span className="text-caption text-text-secondary">
                진행 현장 <strong className="font-bold text-text"><Num n={val(summary?.inProgressWorklogCount)} />개</strong>
              </span>
              <span className="text-xs font-bold text-[color:var(--color-infoStrong)]">확인하기 ›</span>
            </div>
          </Card>

          {/* 현장보고 편집·발송(진입점5, SCR-CS-EDIT) — 구 '고객리포트 설정·미리보기'는 이 화면으로 통합·폐기(설계서 §5.6) */}
          <Card className="agent-card" {...clickable(() => go('customer-reports/edit'))}>
            <div className="agent-card-head">
              <span
                className="flex h-09 w-09 shrink-0 items-center justify-center rounded-lg"
                style={{ background: 'var(--gt-subtle)' }}
              >
                <DocIcon color="var(--gt-primary)" />
              </span>
              <h4 className="agent-tit">현장보고 편집·발송</h4>
            </div>
            <p className="mb-1.5 text-caption font-semibold text-text">고객에게 공사 현황 공유</p>
            <p className="agent-desc flex-1">
              현장보고를 골라 고객께 보낼 메시지·사진을 편집하고 발송합니다.
            </p>
            <div className="meta items-center justify-between">
              <span className="text-caption text-text-secondary">
                공유 중 현장{' '}
                <strong className="font-bold text-text">
                  <Num n={ready ? (summary?.sharingWorklogCount ?? 0) : undefined} />개
                </strong>
              </span>
              <span className="text-xs font-bold text-group">확인하기 ›</span>
            </div>
          </Card>
        </CardList>
        </div>
    </div>
  );
}

export function WorkLogPage() {
  // 메인화면 — 현장목록 패널 없음(대시보드 전용).
  return <WorkLogHome />;
}
